import PlanPoster from '@/shared/assets/media/springboard/plan.webp';
import CreatePoster from '@/shared/assets/media/springboard/create.webp';
import ScalePoster from '@/shared/assets/media/springboard/scale.webp';
import DeliverPoster from '@/shared/assets/media/springboard/deliver.webp';
import PlanVideo from '@/shared/assets/media/springboard/plan.mp4';
import CreateVideo from '@/shared/assets/media/springboard/create.mp4';
import ScaleVideo from '@/shared/assets/media/springboard/scale.mp4';
import DeliverVideo from '@/shared/assets/media/springboard/deliver.mp4';
import { Expand } from 'lucide-react';
import { ROUTES } from '@/shared/config/routes';
import { CATEGORY_COLORS, type ToolCategoryId } from '@/shared/constants/category-colors';
import type { ToolKind } from '@/shared/types/tool-catalog.types';
import type { ToolBadgeIcon } from '@/shared/ui/ToolBadge';
import { i18n } from '@/shared/i18n';

export interface FeaturedCardConfig {
  id: string;
  /** What activating the card does, which is what its call to action says. */
  kind: ToolKind;
  title: string;
  description: string;
  poster: string;
  video: string;
  category: ToolCategoryId;
  /** Replaces the icon the badge looks up from `id`. */
  badgeIcon?: ToolBadgeIcon;
  linkTo: string;
  showOnHub?: boolean;
  requiresFeatureFlag?: string;
}

export const OPPORTUNITIES_ROOM_FEATURED_CARD: FeaturedCardConfig = {
  id: 'opportunities-room',
  kind: 'app',
  // Getters, not values: resolving per read is what lets the copy follow a language change.
  get title() {
    return i18n.t('tools:toolsHub.toolsHubConstants.opportunitiesRoom');
  },
  get description() {
    return i18n.t('tools:toolsHub.toolsHubConstants.spotTimelyBrandSafeOpportunities');
  },
  poster: PlanPoster,
  video: PlanVideo,
  category: 'plan',
  linkTo: ROUTES.TOOLS_OPPORTUNITIES,
};

export const FEATURED_CARDS: readonly FeaturedCardConfig[] = [
  {
    id: 'content-studio',
    kind: 'app',
    get title() {
      return i18n.t('tools:toolsHub.toolsHubConstants.contentStudio');
    },
    get description() {
      return i18n.t('tools:toolsHub.toolsHubConstants.importDesignSystemsAndGenerateAds');
    },
    poster: CreatePoster,
    video: CreateVideo,
    category: 'create',
    linkTo: ROUTES.CONTENT_STUDIO,
    requiresFeatureFlag: 'content_studio_mvp',
  },
  {
    id: 'adaptation',
    kind: 'app',
    get title() {
      return i18n.t('tools:toolsHub.toolsHubConstants.adaptation');
    },
    get description() {
      return i18n.t('tools:toolsHub.toolsHubConstants.scaleVariationsAcrossMarkets');
    },
    poster: ScalePoster,
    video: ScaleVideo,
    category: 'scale',
    badgeIcon: Expand,
    linkTo: ROUTES.TOOLS_SCALE,
    showOnHub: false,
  },
  {
    id: 'quality-assurance',
    kind: 'agent',
    get title() {
      return i18n.t('tools:toolsHub.toolsHubConstants.qualityAssurance');
    },
    get description() {
      return i18n.t('tools:toolsHub.toolsHubConstants.automateBrandAndQuality');
    },
    poster: DeliverPoster,
    video: DeliverVideo,
    category: 'deliver',
    linkTo: ROUTES.TOOLS_DELIVER,
    showOnHub: false,
  },
];

export type ToolsHubCategory = Exclude<keyof typeof CATEGORY_COLORS, 'support'>;

export interface LearnCardInfo {
  label: string;
  subtitle: string;
  poster: string;
  video: string;
}

export interface GuidedLearningCardInfo {
  id: string;
  title: string;
  subtitle: string;
  poster: string;
}

export const GUIDED_LEARNING_CARDS: readonly GuidedLearningCardInfo[] = [
  {
    id: 'strategy-starts-here',
    get title() {
      return i18n.t('tools:toolsHub.toolsHubConstants.strategyStartsHere');
    },
    get subtitle() {
      return i18n.t('tools:toolsHub.toolsHubConstants.stepByStep');
    },
    poster: PlanPoster,
  },
  {
    id: 'find-whats-trending',
    get title() {
      return i18n.t('tools:toolsHub.toolsHubConstants.findWhatSTrending');
    },
    get subtitle() {
      return i18n.t('tools:toolsHub.toolsHubConstants.theOpportunitiesAgent');
    },
    poster: ScalePoster,
  },
  {
    id: 'brand-vs-audience',
    get title() {
      return i18n.t('tools:toolsHub.toolsHubConstants.brandVsAudience');
    },
    get subtitle() {
      return i18n.t('tools:toolsHub.toolsHubConstants.smartPlanningTips');
    },
    poster: DeliverPoster,
  },
  {
    id: 'curate-your-strategy',
    get title() {
      return i18n.t('tools:toolsHub.toolsHubConstants.curateYourStrategy');
    },
    get subtitle() {
      return i18n.t('tools:toolsHub.toolsHubConstants.planningYourFuture');
    },
    poster: PlanPoster,
  },
  {
    id: 'from-trend-to-brief',
    get title() {
      return i18n.t('tools:toolsHub.toolsHubConstants.fromTrendToBriefIn');
    },
    get subtitle() {
      return i18n.t('tools:toolsHub.toolsHubConstants.theFinalStep');
    },
    poster: CreatePoster,
  },
];

export const SCALE_GUIDED_LEARNING_CARDS: readonly GuidedLearningCardInfo[] = [
  {
    id: 'strategy-starts-here-scale',
    get title() {
      return i18n.t('tools:toolsHub.toolsHubConstants.strategyStartsHere');
    },
    get subtitle() {
      return i18n.t('tools:toolsHub.toolsHubConstants.introToPlan');
    },
    poster: PlanPoster,
  },
  {
    id: 'find-whats-trending-scale',
    get title() {
      return i18n.t('tools:toolsHub.toolsHubConstants.findWhatSTrending');
    },
    get subtitle() {
      return i18n.t('tools:toolsHub.toolsHubConstants.theOpportunitiesAgent');
    },
    poster: ScalePoster,
  },
  {
    id: 'brand-vs-audience-scale',
    get title() {
      return i18n.t('tools:toolsHub.toolsHubConstants.brandVsAudience');
    },
    get subtitle() {
      return i18n.t('tools:toolsHub.toolsHubConstants.smartFilteringTips');
    },
    poster: DeliverPoster,
  },
  {
    id: 'curate-your-strategy-scale',
    get title() {
      return i18n.t('tools:toolsHub.toolsHubConstants.curateYourStrategy');
    },
    get subtitle() {
      return i18n.t('tools:toolsHub.toolsHubConstants.pinningTopTrends');
    },
    poster: PlanPoster,
  },
  {
    id: 'from-trend-to-brief-scale',
    get title() {
      return i18n.t('tools:toolsHub.toolsHubConstants.fromTrendToBriefIn');
    },
    get subtitle() {
      return i18n.t('tools:toolsHub.toolsHubConstants.theFinalStep');
    },
    poster: CreatePoster,
  },
];

export const LEARN_CARD_CONFIG: Record<ToolsHubCategory, LearnCardInfo> = {
  plan: {
    get label() {
      return i18n.t('tools:toolsHub.toolsHubConstants.plan');
    },
    get subtitle() {
      return i18n.t('tools:toolsHub.toolsHubConstants.seeAheadShapeStrategy');
    },
    poster: PlanPoster,
    video: PlanVideo,
  },
  create: {
    get label() {
      return i18n.t('tools:toolsHub.toolsHubConstants.create');
    },
    get subtitle() {
      return i18n.t('tools:toolsHub.toolsHubConstants.buildSmartCraftFaster');
    },
    poster: CreatePoster,
    video: CreateVideo,
  },
  scale: {
    get label() {
      return i18n.t('tools:toolsHub.toolsHubConstants.scale');
    },
    get subtitle() {
      return i18n.t('tools:toolsHub.toolsHubConstants.learnFastGrowFaster');
    },
    poster: ScalePoster,
    video: ScaleVideo,
  },
  deliver: {
    get label() {
      return i18n.t('tools:toolsHub.toolsHubConstants.deliver');
    },
    get subtitle() {
      return i18n.t('tools:toolsHub.toolsHubConstants.launchCleanMoveFaster');
    },
    poster: DeliverPoster,
    video: DeliverVideo,
  },
};
